import { useState, useEffect, useMemo, useRef } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { getApiLangFromI18n } from "src/utils/apiLang";
import {
  fetchNotificationSettings,
  fetchNotificationOptions,
  fetchUserLanguagePreference,
  updateUserLanguagePreference,
} from "src/services/MyAccountAPIs";
import {
  updateNotificationChannelMasters,
  updatePaidMediaRule,
  updateSocialRule,
  updateWebAnalyticsRule,
  updateSalesRule,
  deletePaidMediaRule,
  deleteSocialRule,
  deleteWebAnalyticsRule,
  deleteSalesRule,
  fetchPaidMediaPlatforms,
  fetchPaidMediaCampaigns,
  fetchSocialPlatforms,
  fetchWebAnalyticsCities,
  fetchSalesCities,
  fetchSalesProducts,
  type CitiesResponse,
} from "src/services/NotificationAnnouncementsAPIs";
import type {
  ConfigureFormState,
  NotificationChannel,
  NotificationChannelMasters,
  NotificationOptionsResponse,
  NotificationSection,
  NotificationSystem,
  PageTarget,
  PaidMediaCampaign,
  SalesProduct,
  SocialPlatformData,
} from "../Types/NotificationTypes";
import { DEFAULT_FORM } from "../Utils/NotificationConstants";
import { GetApiErrorMessage } from "../Utils/ErrorMessage";
import {
  buildConfigureForm,
  getCityFetcher,
  getComparisonOptionsForItem,
  getPeriodOptionsForItem,
  savePaidMediaRule,
  saveSalesRule,
  saveSocialRule,
  saveWebAnalyticsRule,
  updateRuleChannel,
} from "../Utils/NotificationRuleHelpers";

export const useNotificationPreferences = () => {
  const [sections, setSections] = useState<NotificationSection[]>([]);
  const [expanded, setExpanded] = useState<boolean[]>([]);
  const [options, setOptions] = useState<NotificationOptionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [digestFrequency, setDigestFrequency] = useState<string>("daily");
  const [savingMaster, setSavingMaster] = useState(false);

  const [preferredNotificationLanguage, setPreferredNotificationLanguage] =
    useState<"en" | "ar">("en");
  const [savingLanguage, setSavingLanguage] = useState(false);

  const channelMasters = useMemo<NotificationChannelMasters>(
    () => ({
      email: sections.some(section => section.items.some(item => item.rule?.email)),
      push: sections.some(section => section.items.some(item => item.rule?.push)),
      in_app: sections.some(section => section.items.some(item => item.rule?.in_app)),
    }),
    [sections]
  );

  const [configureTarget, setConfigureTarget] = useState<{
    sectionIdx: number;
    itemIdx: number;
  } | null>(null);
  const [form, setForm] = useState<ConfigureFormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState<number | null>(null);
  const configurePanelRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (configureTarget) {
      configurePanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [configureTarget]);

  // Picker data
  const [availablePlatforms, setAvailablePlatforms] = useState<{ key: string; label: string }[]>([]);
  const [availableCampaigns, setAvailableCampaigns] = useState<PaidMediaCampaign[]>([]);
  const [campaignSearch, setCampaignSearch] = useState<string>("");
  const [socialPlatforms, setSocialPlatforms] = useState<SocialPlatformData[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [citySearch, setCitySearch] = useState<string>("");
  const [cityAvailable, setCityAvailable] = useState<boolean>(true);
  const [availableProducts, setAvailableProducts] = useState<SalesProduct[]>([]);
  const [productSearch, setProductSearch] = useState<string>("");
  const [productAvailable, setProductAvailable] = useState<boolean>(true);

  const { t, i18n } = useTranslation();

  const apiLang = getApiLangFromI18n(i18n);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchUserLanguagePreference();
        if (!cancelled && (res.language === "en" || res.language === "ar")) {
          setPreferredNotificationLanguage(res.language);
        }
      } catch (err) {
        console.error("Error fetching preferred notification language:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePreferredLanguageChange = async (language: "en" | "ar") => {
    if (language === preferredNotificationLanguage || savingLanguage) {return;}
    const previous = preferredNotificationLanguage;
    setPreferredNotificationLanguage(language);
    setSavingLanguage(true);
    try {
      const res = await updateUserLanguagePreference(language);
      if (res.message) {
        message.success(res.message);
      }
    } catch (err) {
      console.error("Error updating preferred notification language:", err);
      setPreferredNotificationLanguage(previous);
      message.error(
        GetApiErrorMessage(
          err,
          t("notificationPreferences.errors.languageUpdateFailed", {
            defaultValue: "Could not update your preferred notification language.",
          })
        )
      );
    } finally {
      setSavingLanguage(false);
    }
  };

  const fetchData = async (lang: "en" | "ar") => {
    try {
      setLoading(true);
      const [settingsRes, optionsRes] = await Promise.all([
        fetchNotificationSettings(lang),
        fetchNotificationOptions(lang),
      ]);
      const secs = settingsRes.notification_sections ?? [];
      setSections(secs);
      setExpanded(prev =>
        prev.length === secs.length ? prev : new Array(secs.length).fill(false)
      );
      setOptions(optionsRes);
      setDigestFrequency(settingsRes.notification_refresh_frequency ?? "daily");
    } catch (err) {
      console.error("Error fetching notification data:", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    try {
      const settingsRes = await fetchNotificationSettings(apiLang);
      setSections(settingsRes.notification_sections ?? []);
    } catch (err) {
      console.error("Error refreshing notification settings:", err);
    }
  };

  useEffect(() => {
    fetchData(apiLang);
  }, [apiLang]);

  // Reload campaigns when platforms or search changes
  useEffect(() => {
    if (!configureTarget) {return;}
    const section = sections[configureTarget.sectionIdx];
    if (section?.system !== "paid_media" || form.platforms.length === 0) {
      setAvailableCampaigns([]);
      return;
    }
    fetchPaidMediaCampaigns(form.platforms, campaignSearch)
      .then(data => setAvailableCampaigns(data.campaigns ?? []))
      .catch(() => {});
  }, [form.platforms, campaignSearch, configureTarget]);

  // Reload cities when search changes (web analytics + sales use different endpoints)
  useEffect(() => {
    if (!configureTarget) {return;}
    const section = sections[configureTarget.sectionIdx];
    const cityFetcher = getCityFetcher(section?.system);
    if (!cityFetcher) {return;}
    cityFetcher(citySearch, apiLang)
      .then((data: CitiesResponse) => {
        setAvailableCities(data.cities ?? []);
        if (!citySearch.trim()) {setCityAvailable(data.available);}
      })
      .catch(() => {});
  }, [citySearch, configureTarget, apiLang]);

  useEffect(() => {
    if (!configureTarget) {return;}
    const section = sections[configureTarget.sectionIdx];
    if (section?.system !== "sales") {return;}
    fetchSalesProducts(productSearch, apiLang)
      .then(data => {
        setAvailableProducts(data.products ?? []);
        if (!productSearch.trim()) {setProductAvailable(data.available);}
      })
      .catch(() => {});
  }, [productSearch, configureTarget, apiLang]);

  const toggleSection = (idx: number) =>
    setExpanded(prev => prev.map((v, i) => (i === idx ? !v : v)));

  const fetchPickerData = (system: NotificationSystem) => {
    if (system === "paid_media") {
      fetchPaidMediaPlatforms()
        .then(data => setAvailablePlatforms(data.platforms ?? []))
        .catch(() => {});
    } else if (system === "social") {
      fetchSocialPlatforms(apiLang)
        .then(data => setSocialPlatforms(data.platforms ?? []))
        .catch(() => {});
    } else if (system === "sales") {
      fetchSalesCities("", apiLang)
        .then(data => {
          setAvailableCities(data.cities ?? []);
          setCityAvailable(data.available);
        })
        .catch(() => {});
      fetchSalesProducts("", apiLang)
        .then(data => {
          setAvailableProducts(data.products ?? []);
          setProductAvailable(data.available);
        })
        .catch(() => {});
    } else {
      fetchWebAnalyticsCities("", apiLang)
        .then(data => {
          setAvailableCities(data.cities ?? []);
          setCityAvailable(data.available);
        })
        .catch(() => {});
    }
  };

  const handleOpenConfigure = (sectionIdx: number, itemIdx: number) => {
    const section = sections[sectionIdx];
    const item = section.items[itemIdx];
    const defaultPeriod =
      getPeriodOptionsForItem(section.system, item, options)[0]?.value ??
      DEFAULT_FORM.period;
    const defaultComparison =
      getComparisonOptionsForItem(section.system, item, options)[0]?.value ??
      DEFAULT_FORM.comparison_period;

    setForm(
      buildConfigureForm(
        section.system,
        item.rule,
        defaultPeriod,
        defaultComparison
      )
    );
    setCampaignSearch("");
    setCitySearch("");
    setProductSearch("");
    setAvailableCampaigns([]);
    setAvailableCities([]);
    setAvailableProducts([]);
    setConfigureTarget({ sectionIdx, itemIdx });

    fetchPickerData(section.system);
  };

  const handleToggle = async (
    sectionIdx: number,
    itemIdx: number,
    channel: NotificationChannel,
    checked: boolean
  ) => {
    const item = sections[sectionIdx].items[itemIdx];
    if (!item.rule) {return;}
    const ruleId = item.rule.rule_id;

    setSections(prev => updateRuleChannel(prev, sectionIdx, itemIdx, channel, checked));

    try {
      const payload = { [channel]: checked };
      if (sections[sectionIdx].system === "paid_media") {
        await updatePaidMediaRule(ruleId, payload);
      } else if (sections[sectionIdx].system === "social") {
        await updateSocialRule(ruleId, payload);
      } else if (sections[sectionIdx].system === "sales") {
        await updateSalesRule(ruleId, payload);
      } else {
        await updateWebAnalyticsRule(ruleId, payload);
      }
    } catch (err) {
      console.error("Error toggling notification channel:", err);
      message.error(
        GetApiErrorMessage(
          err,
          t("notificationPreferences.errors.toggleFailed", {
            defaultValue: "Could not update the notification channel.",
          })
        )
      );
      await refreshSettings();
    }
  };

  const handleMasterToggleChange = async (
    channel: NotificationChannel,
    checked: boolean
  ) => {
    setSavingMaster(true);
    try {
      const response = await updateNotificationChannelMasters({ [channel]: checked });
      if (response.changes[channel]?.action === "no_configured_rules") {
        message.warning(
          t("notificationPreferences.errors.noConfiguredRules", {
            defaultValue: "Set up at least one notification before enabling this channel.",
          })
        );
      }
      await refreshSettings();
    } catch (err) {
      console.error("Error updating channel master:", err);
      message.error(
        GetApiErrorMessage(
          err,
          t("notificationPreferences.errors.masterToggleFailed", {
            defaultValue: "Could not update the channel setting.",
          })
        )
      );
    } finally {
      setSavingMaster(false);
    }
  };

  const handleDeleteRule = async (sectionIdx: number, itemIdx: number) => {
    const item = sections[sectionIdx].items[itemIdx];
    if (!item.rule) {return;}
    const ruleId = item.rule.rule_id;
    setDeletingRuleId(ruleId);
    try {
      if (sections[sectionIdx].system === "paid_media") {
        await deletePaidMediaRule(ruleId);
      } else if (sections[sectionIdx].system === "social") {
        await deleteSocialRule(ruleId);
      } else if (sections[sectionIdx].system === "sales") {
        await deleteSalesRule(ruleId);
      } else {
        await deleteWebAnalyticsRule(ruleId);
      }
      if (
        configureTarget?.sectionIdx === sectionIdx &&
        configureTarget?.itemIdx === itemIdx
      ) {
        setConfigureTarget(null);
      }
      await refreshSettings();
    } catch (err) {
      console.error("Error deleting rule:", err);
      message.error(
        GetApiErrorMessage(
          err,
          t("notificationPreferences.errors.deleteFailed", {
            defaultValue: "Could not remove the rule.",
          })
        )
      );
    } finally {
      setDeletingRuleId(null);
    }
  };

  const handleSaveThreshold = async () => {
    if (!configureTarget) {return;}
    const { sectionIdx, itemIdx } = configureTarget;
    const section = sections[sectionIdx];
    const item = section.items[itemIdx];
    const isEdit = item.configured && item.rule !== null;
    setSaving(true);
    try {
      if (section.system === "paid_media") {
        await savePaidMediaRule(item, form, isEdit);
      } else if (section.system === "social") {
        await saveSocialRule(item, form, isEdit);
      } else if (section.system === "sales") {
        await saveSalesRule(item, form, isEdit);
      } else {
        await saveWebAnalyticsRule(item, form, isEdit);
      }
      await refreshSettings();
      setConfigureTarget(null);
    } catch (err) {
      console.error("Error saving rule:", err);
      message.error(
        GetApiErrorMessage(
          err,
          t("notificationPreferences.errors.saveFailed", {
            defaultValue: "Could not save the rule.",
          })
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePlatformsChange = (val: unknown) => {
    const next = (val as string[]) ?? [];
    setForm(f => ({
      ...f,
      platforms: next,
      campaigns: f.campaigns.filter(c => next.includes(c.platform)),
    }));
  };

  const handleCampaignsChange = (val: unknown, allCampaigns: PaidMediaCampaign[]) => {
    const ids = (val as string[]) ?? [];
    const map = new Map(allCampaigns.map(c => [c.campaign_id, c] as const));
    setForm(f => ({
      ...f,
      campaigns: ids.map(id => map.get(id)).filter((c): c is PaidMediaCampaign => Boolean(c)),
    }));
  };

  const handleSocialPlatformsChange = (val: unknown) => {
    const plats = (val as string[]) ?? [];
    setForm(f => ({
      ...f,
      social_platforms: plats,
      targets: f.targets.filter(tgt => plats.includes(tgt.platform)),
    }));
  };

  const handleTargetsChange = (val: unknown, availablePages: PageTarget[]) => {
    const keys = (val as string[]) ?? [];
    const map = new Map<string, PageTarget>(
      availablePages.map(p => [`${p.platform}::${p.page_id}`, p])
    );
    setForm(f => ({
      ...f,
      targets: keys.map(k => map.get(k)).filter((tgt): tgt is PageTarget => Boolean(tgt)),
    }));
  };

  const handleCitiesChange = (val: unknown) => {
    setForm(f => ({ ...f, cities: (val as string[]) ?? [] }));
  };

  const handleProductsChange = (val: unknown, allProducts: SalesProduct[]) => {
    const ids = (val as string[]) ?? [];
    const map = new Map(allProducts.map(p => [p.product_id, p] as const));
    setForm(f => ({
      ...f,
      products: ids
        .map(id => map.get(id))
        .filter((p): p is SalesProduct => Boolean(p)),
    }));
  };

  const closeConfigure = () => setConfigureTarget(null);

  const frequencyOptions = options?.frequencies ?? [
    { value: "daily", label: "Daily" },
    { value: "6h", label: "Every 6 Hours" },
  ];

  const digestDisplay =
    frequencyOptions.find(f => f.value === digestFrequency)?.label ?? digestFrequency;

  const digestNote = t("notificationPreferences.digestNote", {
    frequency: digestDisplay,
    defaultValue: `${digestDisplay} notifications will be sent at 10:00 AM for all your configured and enabled email or push alerts.`,
  });

  const hasNoConfiguredNotifications = sections.every(section =>
    section.items.every(item => !item.configured)
  );

  return {
    t,
    i18n,
    sections,
    expanded,
    options,
    loading,
    digestFrequency,
    savingMaster,
    preferredNotificationLanguage,
    savingLanguage,
    channelMasters,
    configureTarget,
    form,
    setForm,
    saving,
    deletingRuleId,
    configurePanelRef,
    availablePlatforms,
    availableCampaigns,
    socialPlatforms,
    availableCities,
    cityAvailable,
    citySearch,
    availableProducts,
    productAvailable,
    productSearch,
    frequencyOptions,
    digestNote,
    hasNoConfiguredNotifications,
    toggleSection,
    handlePreferredLanguageChange,
    handleOpenConfigure,
    handleToggle,
    handleMasterToggleChange,
    handleDeleteRule,
    handleSaveThreshold,
    handlePlatformsChange,
    handleCampaignsChange,
    handleSocialPlatformsChange,
    handleTargetsChange,
    handleCitiesChange,
    handleProductsChange,
    closeConfigure,
    setCampaignSearch,
    setCitySearch,
    setProductSearch,
  };
};
